/**
 * Shared avatar helpers — the ONE place the player app derives an initials mark
 * and decides whether a stored avatar URL is safe to put in an `<img src>`.
 *
 * Both the app-bar `<Avatar/>` and the Me-slice `<ProfileSummary/>` consume these
 * so the initials rule and the https guard live in exactly one place (no
 * duplicated logic that can drift). ProfileSummary's existing "PP" / avatar
 * output stays byte-identical because it now calls the same functions.
 */

/**
 * Derive an initials mark from a display name.
 *
 *   "Pat Player" → "PP"   (first + last initial)
 *   "Lin"        → "LI"   (single word → first two letters)
 *   ""/"   "     → "?"    (no usable name — callers that want a person icon
 *                          instead should branch on `hasName()` first)
 */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * True when `name` carries a usable identity — at least one LETTER or NUMBER.
 *
 * Trimming whitespace is not enough: a symbol/emoji-only name ("!!!", "🎉🎉",
 * "·") has non-whitespace chars but mints garbage initials ("!!", "🎉", "·") as
 * the user's mark. Requiring a `\p{L}|\p{N}` character makes such junk-only names
 * fall through to the person icon, matching Avatar's documented contract
 * ("blank/no name → person icon, never garbage initials").
 */
export function hasName(name?: string | null): name is string {
  return !!name && /[\p{L}\p{N}]/u.test(name);
}

/**
 * Defense in depth: a legacy / poisoned row could carry a non-https avatar
 * (javascript:, data:, http:, protocol-relative //…). The thin client does NOT
 * blindly trust the stored value into an `<img src>` — only an `https://` URL is
 * rendered as an image; anything else falls back to initials / the person icon.
 */
export function hasSafeAvatar(url?: string | null): url is string {
  return url?.startsWith("https://") ?? false;
}
